import { useState } from 'react';
import { useAppContext } from '../../../contexts/AppContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { useLembretesContext } from '../Context';
import LembretesService from '../Service';
import { Bell, ChevronRight, TriangleAlert } from 'lucide-react';

const channelLabel: Record<string, string> = {
    WHATSAPP: 'WhatsApp',
    PUSH: 'Push',
    EMAIL: 'E-mail',
};

const getScheduleLabel = (row: any) => {
    if (row.triggerType === 'AFTER_COMPLETION') {
        const days = Number(row.daysAfterCompletion);
        return `${days} ${days === 1 ? 'dia' : 'dias'} após o atendimento`;
    }

    return `Todo dia ${row.dayOfMonth}`;
};

const getAudienceLabel = (row: any) => {
    const clients = Number(row.targetSummary?.clientsCount || 0);
    const groups = Number(row.targetSummary?.groupsCount || 0);
    const parts = [
        clients ? `${clients} ${clients === 1 ? 'cliente' : 'clientes'}` : '',
        groups ? `${groups} ${groups === 1 ? 'grupo' : 'grupos'}` : '',
    ].filter(Boolean);

    return parts.join(' e ') || 'Sem público';
};

export default function LembretesList() {
    const { data, loading, error, reloadData } = useLembretesContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { user } = useAuthContext();
    const { setParams } = useParamsContext();
    const [togglingId, setTogglingId] = useState('');
    const canManage = !!(user?.root || user?.admin);

    const toggleStatus = async (row: any) => {
        const nextStatus = row.status === 'INACTIVE' ? 'ACTIVE' : 'INACTIVE';

        try {
            setTogglingId(row._id);
            await LembretesService.update(row._id, { status: nextStatus });
            await reloadData();
            successSnackbar(nextStatus === 'ACTIVE' ? 'Lembrete ativado.' : 'Lembrete desativado.');
        } catch (e) {
            console.error('Erro ao alterar status do lembrete:', e);
            errorSnackbar('Não foi possível alterar o lembrete.');
        } finally {
            setTogglingId('');
        }
    };

    if (loading) {
        return (
            <div className="space-y-2 px-2" aria-busy="true">
                {[0, 1, 2].map((item) => (
                    <div key={item} className="sf-skeleton h-16" />
                ))}
            </div>
        );
    }

    if (error && !data.length) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar os lembretes</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void reloadData()}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!data.length) {
        return (
            <div className="sf-state">
                <Bell aria-hidden />
                <h2>Nenhum lembrete criado</h2>
                <p>Lembre seus clientes de agendar em um dia fixo do mês ou alguns dias depois do atendimento.</p>
                {canManage && (
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => setParams({ action: 'add' })}>
                        Criar lembrete
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="sf-page mx-auto max-w-4xl px-2">
            <ul className="sf-group">
                {data.map((row) => {
                    const inactive = row.status === 'INACTIVE';
                    const content = (
                        <span className={`sf-row-body ${inactive ? 'opacity-60' : ''}`}>
                            <span className="sf-row-title">
                                {getScheduleLabel(row)} às {row.sendTime}
                            </span>
                            <span className="sf-row-sub">
                                {(row.channels || []).map((channel: string) => channelLabel[channel] || channel).join(', ') || 'Sem canal'}
                                {' · '}
                                {getAudienceLabel(row)}
                            </span>
                        </span>
                    );

                    return (
                        <li key={row._id}>
                            {canManage ? (
                                <div className="sf-row !p-0">
                                    <button
                                        type="button"
                                        className="sf-row min-w-0 flex-1 !pr-2"
                                        onClick={() => setParams({ action: 'edit', id: row._id })}
                                    >
                                        {content}
                                        <ChevronRight className="sf-chevron" aria-hidden />
                                    </button>
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-success toggle-sm mr-3.5 shrink-0"
                                        checked={!inactive}
                                        onChange={() => toggleStatus(row)}
                                        disabled={togglingId === row._id}
                                        aria-label={`${getScheduleLabel(row)} ativo`}
                                    />
                                </div>
                            ) : (
                                <div className="sf-row">
                                    {content}
                                    {inactive && (
                                        <span className="sf-tag shrink-0" data-tone="gray">
                                            Inativo
                                        </span>
                                    )}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
